import type { Product, Size, StyleVariant } from '../../../utilities/graphql/codegen';
import type { InsightsMethodMap } from 'search-insights';

import { useState } from 'react';

import { useAlgoliaInsights } from '../../../components/shared/vendors/useAlgoliaInsights';
import {
  CART_ENTRIES,
  CART_MUTATED,
  CART_STORAGE_VALUE,
} from '../../../utilities/constants/localStorageKeys';
import { useCartContext } from '../../../utilities/context/dynamic/CartContext';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useDataLayerContext } from '../../../utilities/context/static/DataLayerContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { useAddToCartMutation } from '../../../utilities/graphql/codegen';
import { storageAvailable } from '../../../utilities/helpers';
import { getProductDataLayerEvent, parseCartUpdateDataLayer } from '../../../utilities/vendors';

const defaultErrors = {
  generic500Error: 'Something went wrong. Please try again later.',
};

export type useAddToCartReturnType = {
  isProcessing: boolean;
  isPresentInCart: boolean;
  quantityOfStyleInCart: number;
  addToCart: () => void;
  cartMutationError: string;
};

interface IUseAddToCartProps {
  product?: Product;
  variant?: StyleVariant | null;
  size?: Size | null;
  hasLocalStorage?: boolean;
  hasAlgoliaEvent?: boolean;
  hasExponeaEvent?: boolean;
  hasDataLayerEvent?: boolean;
  algoliaAddToCartEventName?: string;
  fromQuickView?: boolean;
  errors?: {
    generic500Error: string;
  };
}

function useAddToCart({
  product,
  variant,
  size,
  hasLocalStorage = true,
  hasAlgoliaEvent = false,
  hasExponeaEvent = false,
  hasDataLayerEvent = false,
  algoliaAddToCartEventName = 'PDP_addToBasket_convertedObjectIDs',
  fromQuickView = false,
  errors = defaultErrors,
}: IUseAddToCartProps): useAddToCartReturnType {
  // CONTEXTS
  const {
    configuration: { enableExponea },
  } = useStaticContext();
  const { locale, country, siteType } = useAppContext();
  const { setMiniCart, setCartHovered, miniCart } = useCartContext();
  const { pushToDataLayer } = useDataLayerContext();

  // STATES
  const [cartMutationError, setCartMutationError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // HOOKS & MUTATIONS
  const [addToCartMutation] = useAddToCartMutation();
  const { pushToAlgolia } = useAlgoliaInsights();

  // CHECK IF PRODUCT IS PRESENT IN CART
  const isPresentInCart = !!miniCart?.entries?.find(
    entry => entry?.product?.baseProduct === variant?.code
  );

  // DETERMINE HOW MANY OF THIS VARIANT ARE IN THE CART
  let quantityOfStyleInCart = 0;

  miniCart?.entries?.forEach(entry => {
    if (entry?.product?.baseProduct?.toLocaleLowerCase() === variant?.code?.toLocaleLowerCase()) {
      quantityOfStyleInCart += entry?.quantity || 0;
    }
  });

  // sonarlint rule failing: cyclomatic complexity
  const addToCart = /* NOSONAR */ async () => {
    if (!product) {
      // eslint-disable-next-line no-console
      console.log('addToCart called without a valid product');

      return;
    }

    let productCode = product.baseProduct || product.code;

    if (variant) {
      productCode = variant.code;
    }

    if (product.oneSize || product.isGiftCard) {
      productCode = `${productCode}-PC`;
    }

    if (size?.value) {
      productCode = size.code || `${productCode}-${size.value}`;
    }

    try {
      setIsProcessing(true);
      setCartMutationError('');

      const response = await addToCartMutation({
        variables: {
          siteType,
          code: productCode,
          locale,
        },
      });

      const cartData = response.data;
      const success = cartData?.addToCart?.cartModifications?.[0]?.statusCode === 'success';

      if (!success) {
        // NO SUCCESS
        setCartMutationError(cartData?.addToCart?.cartModifications?.[0]?.statusMessage || '');
      } else if (cartData?.addToCart?.cart) {
        // SUCCESS

        // SAVE IN LOCAL STORAGE
        if (hasLocalStorage && storageAvailable('localStorage')) {
          localStorage.setItem(`${country}${CART_MUTATED}`, CART_STORAGE_VALUE);
          localStorage.setItem(
            `${CART_ENTRIES}_${locale}`,
            JSON.stringify(cartData?.addToCart?.cart)
          );
        }

        // ALGOLIA
        if (hasAlgoliaEvent) {
          const event: keyof InsightsMethodMap = 'convertedObjectIDs';
          const eventName = algoliaAddToCartEventName || '';
          const includeQueryId = !fromQuickView;

          pushToAlgolia(event, eventName, product?.algoliaObjectId, includeQueryId);
        }

        // EXPONEA
        if (hasExponeaEvent && enableExponea) {
          const exponeaCartUpdateProperties = parseCartUpdateDataLayer('add', cartData?.addToCart);

          pushToDataLayer({
            event: 'exponea_cart_update',
            event_name: 'cart_update',
            namespace: 'exponea',
            event_properties: exponeaCartUpdateProperties,
          });
        }

        // DATA LAYER
        if (hasDataLayerEvent) {
          const cartProduct = cartData?.addToCart?.cart?.entries?.find(
            entry => entry?.product?.code?.toLowerCase() === productCode.toLowerCase()
          )?.product;
          const dataLayerProduct = cartProduct || {
            ...product,
            baseProduct: product.baseProduct || product.code,
            code: productCode,
            size: size?.value,
            ean: size?.ean,
          };
          const eecAddToCartEvent = getProductDataLayerEvent('eecAddToCart', {
            product: dataLayerProduct,
            quantity: 1,
          });

          if (eecAddToCartEvent) {
            pushToDataLayer(eecAddToCartEvent);
          }
        }

        // OPEN MINI CART
        setMiniCart(cartData?.addToCart?.cart);
        setCartHovered(true);

        // CLOSE MINI CART AFTER 3 SECONDS
        setTimeout(() => setCartHovered(false), 3000);
      }

      setIsProcessing(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error - AddToCart via ProductAddActions', error);
      setIsProcessing(false);
      setCartMutationError(errors?.generic500Error || defaultErrors.generic500Error);
    }
  };

  return { isProcessing, isPresentInCart, quantityOfStyleInCart, addToCart, cartMutationError };
}

export { useAddToCart };
